import { useEffect, useRef, useState } from 'react';
import { Card, useToast } from '@kss/ui';
import { useAuth } from '@kss/auth';

interface Pref {
  id: string;
  label: string;
  hint: string;
  enabled: boolean;
}

const DEFAULTS: Pref[] = [
  { id: 'orders',     label: 'Order updates',     hint: 'Status changes, dispatch, delivery alerts', enabled: true  },
  { id: 'promos',     label: 'Promotions',        hint: 'Sales, coupons, lightning deals',           enabled: false },
  { id: 'newsletter', label: 'Weekly newsletter', hint: 'New arrivals & editor picks',                enabled: false },
  { id: 'sms',        label: 'SMS alerts',        hint: 'Critical order updates over SMS',           enabled: true  },
];

const STORAGE_PREFIX = 'kss.notifications.v1';

function storageKey(userId?: string | null): string {
  return `${STORAGE_PREFIX}:${userId ?? 'guest'}`;
}

/**
 * Load saved preferences and merge with the canonical default
 * shape — that way new toggles added in code (e.g. "WhatsApp
 * alerts") show up for existing customers without losing the
 * choices they already made.
 */
function readPrefs(userId?: string | null): Pref[] {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (raw) {
      const stored = JSON.parse(raw) as Pref[];
      const byId = new Map(stored.map((p) => [p.id, p]));
      return DEFAULTS.map((d) => byId.get(d.id) ?? d);
    }
  } catch {
    /* malformed JSON — fall back to defaults */
  }
  return DEFAULTS;
}

/**
 * Channel & topic preferences.
 *
 * Persists per-user to `localStorage` (`kss.notifications.v1:<userId>`)
 * until the backend exposes a `/me/preferences` endpoint. Once that
 * ships, swap the read/write helpers here for a service call —
 * the rest of the page stays untouched.
 */
export default function AccountNotifications() {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const toast = useToast();

  const [prefs, setPrefs] = useState<Pref[]>(() => readPrefs(userId));
  const lastUserId = useRef<string | null>(userId);
  // Skip the toast on the very first persist (initial mount). It
  // would be confusing to see "Saved" before the user has touched
  // anything.
  const skipNextToast = useRef(true);

  useEffect(() => {
    if (lastUserId.current === userId) return;
    lastUserId.current = userId;
    skipNextToast.current = true;
    setPrefs(readPrefs(userId));
  }, [userId]);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey(userId), JSON.stringify(prefs));
    } catch {
      /* storage full / disabled — silent */
    }
    if (skipNextToast.current) {
      skipNextToast.current = false;
      return;
    }
    // Lightweight "saved" feedback — keeps the page feeling
    // responsive without a full success card per toggle.
    toast.success('Preferences saved');
    // toast is stable from the provider; deliberately not in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefs, userId]);

  const toggle = (id: string) =>
    setPrefs((p) => p.map((x) => (x.id === id ? { ...x, enabled: !x.enabled } : x)));

  return (
    <div className="grid gap-3">
      <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">Notifications</h1>
      <Card className="divide-y divide-[var(--color-border)] p-0">
        {prefs.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-4 px-5 py-4">
            <div>
              <p className="text-sm font-medium text-[var(--color-text-primary)]">{p.label}</p>
              <p className="text-xs text-[var(--color-text-secondary)]">{p.hint}</p>
            </div>
            <label className="inline-flex cursor-pointer items-center">
              <input
                type="checkbox"
                checked={p.enabled}
                onChange={() => toggle(p.id)}
                className="peer sr-only"
              />
              <span className="relative h-5 w-9 rounded-full bg-[var(--color-bg-card-hover)] peer-checked:bg-[var(--color-primary)]">
                <span
                  className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${
                    p.enabled ? 'left-[18px]' : 'left-0.5'
                  }`}
                />
              </span>
            </label>
          </div>
        ))}
      </Card>
    </div>
  );
}
